/**
 * DevToolsPanel -- Chrome DevTools-style panel with Console, State, Network, Spec Compliance tabs.
 *
 * Rendered inside a vertical resizable PanelGroup in StudioLayout.
 * Console tab shows unified chronological log. State tab shows live JSON tree.
 * Network tab captures API calls via iframe bridge. Spec Compliance tab runs validation.
 */

import { X } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import { useDevToolsStore, type DevToolsBottomTab } from '@/features/devtools/store/devtools-store'
import { ConsoleTab } from '@/features/devtools/components/console-tab'
import { StateTab } from '@/features/devtools/components/state-tab'
import { NetworkTab } from '@/features/devtools/components/network-tab'
import { SpecComplianceTab } from '@/features/devtools/components/spec-compliance-tab'
import { KgValidateTab } from '@/features/devtools/components/kg-validate-tab'

const TABS: Array<{ id: DevToolsBottomTab; label: string }> = [
  { id: 'console', label: 'Console' },
  { id: 'state', label: 'State' },
  { id: 'network', label: 'Network' },
  { id: 'spec-compliance', label: 'Spec Compliance' },
  // Operator surface: SHACL validation + fallback-promotion control tower.
  { id: 'kg-validate', label: 'KG Validate' },
]

export function DevToolsPanel() {
  const runtime = useDevToolsStore((s) => s.runtime)
  const activeBottomTab = useDevToolsStore((s) => s.activeBottomTab)
  const setActiveBottomTab = useDevToolsStore((s) => s.setActiveBottomTab)

  return (
    <div className="flex flex-col h-full bg-background border-t border-border">
      {/* Tab bar */}
      <div className="flex items-center border-b border-border px-1 gap-px shrink-0">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveBottomTab(tab.id)}
            className={`px-2 py-1.5 text-xs transition-colors ${
              activeBottomTab === tab.id
                ? 'text-foreground border-b-2 border-primary font-medium'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
        {/* Runtime indicator */}
        <div className="ml-auto pr-2 flex items-center gap-1">
          <div className={`w-1.5 h-1.5 rounded-full ${runtime ? 'bg-success' : 'bg-muted-foreground/40'}`} />
          <span className="text-[10px] text-muted-foreground">{runtime ? 'Live' : 'Off'}</span>
        </div>
        {/* Close button */}
        <button
          onClick={() => useStudioStore.getState().setDevToolsPanelOpen(false)}
          className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {activeBottomTab === 'console' && <ConsoleTab />}
        {activeBottomTab === 'state' && <StateTab />}
        {activeBottomTab === 'network' && <NetworkTab />}
        {activeBottomTab === 'spec-compliance' && <SpecComplianceTab />}
        {activeBottomTab === 'kg-validate' && <KgValidateTab />}
      </div>
    </div>
  )
}
